import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, MapPinOff, SlidersHorizontal } from "lucide-react";
import { Listing } from "../api/listings-client";
import { ListingCard } from "./ListingCard";

const NoResultsFound = () => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      {/* Animated illustration */}
      <div className="relative mb-8 group">
        {/* Multiple pulsing circles with different speeds and sizes */}
        <div className="absolute inset-[-30px] bg-gradient-to-br from-[#24ccf2]/30 via-[#2476f2]/30 to-purple-500/30 rounded-full blur-xl animate-[pulse_3s_ease-in-out_infinite]" />
        <div className="absolute inset-[-20px] bg-gradient-to-br from-[#24ccf2]/20 via-[#2476f2]/20 to-purple-500/20 rounded-full blur-lg animate-[pulse_2s_ease-in-out_infinite]" />
        <div className="absolute inset-[-10px] bg-gradient-to-br from-[#24ccf2]/10 via-[#2476f2]/10 to-purple-500/10 rounded-full blur-md animate-[pulse_1.5s_ease-in-out_infinite]" />

        {/* Main icon container with hover effect */}
        <div className="relative bg-gradient-to-br from-[#24ccf2] to-[#2476f2] rounded-full p-4 transition-transform duration-300 hover:scale-110">
          <div className="animate-[rock_2s_ease-in-out_infinite] origin-bottom" style={{
            animation: 'rock 2s ease-in-out infinite',
            '@keyframes rock': {
              '0%, 100%': { transform: 'rotate(-10deg)' },
              '50%': { transform: 'rotate(10deg)' }
            }
          }}>
            <MapPinOff className="w-12 h-12 text-white" />
          </div>
        </div>

        {/* Small decorative dots */}
        <div className="absolute top-0 right-0 w-2 h-2 bg-[#24ccf2] rounded-full animate-[ping_2s_ease-in-out_infinite]" />
        <div className="absolute bottom-0 left-0 w-2 h-2 bg-[#2476f2] rounded-full animate-[ping_2s_ease-in-out_infinite_500ms]" />
        <div className="absolute top-0 left-0 w-2 h-2 bg-purple-500 rounded-full animate-[ping_2s_ease-in-out_infinite_1000ms]" />
      </div>

      {/* Main heading with gradient text */}
      <h3 className="text-2xl md:text-3xl font-semibold text-center mb-3 bg-gradient-to-r from-[#24ccf2] via-[#2476f2] to-purple-500 bg-clip-text text-transparent">
        No listings found in this area
      </h3>

      {/* Friendly message */}
      <p className="text-gray-600 text-center max-w-md mb-8">
        Try expanding your search or checking back later – new listings are always being added!
      </p>

      <style jsx>{`
        @keyframes rock {
          0%, 100% { transform: rotate(-5deg); }
          50% { transform: rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

// Responsive Pagination component with maximized space utilization
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  // State for tracking pagination elements
  const [paginationState, setPaginationState] = useState({
    visiblePageNumbers: [],
    showNavButtons: true
  });

  const paginationRef = useRef(null);

  // Calculate optimal pagination based on container width
  // This is more of an estimate than anything and so can break easily
  useEffect(() => {
    const calculatePagination = () => {
      if (!paginationRef.current) return;

      const containerWidth = paginationRef.current.offsetWidth;

      // Use your actual measurements
      const navButtonWidth = 38; // px (measured)
      const pageButtonWidth = 36; // px (measured average)
      const spacing = 4;         // px (from space-x-1)

      // Step 1: Determine if we should show nav buttons
      // First, calculate how many elements we could fit with & without nav buttons
      const navButtonsSpace = 2 * (navButtonWidth + spacing);
      const spaceWithoutNavButtons = containerWidth;
      const spaceWithNavButtons = containerWidth - navButtonsSpace;

      // Calculate how many page buttons we can fit in each scenario
      const maxButtonsWithNav = Math.floor(spaceWithNavButtons / (pageButtonWidth + spacing));
      const maxButtonsWithoutNav = Math.floor(spaceWithoutNavButtons / (pageButtonWidth + spacing));

      // Decide if we should show nav buttons based on space efficiency
      // If removing nav buttons lets us show 3+ more page numbers, hide them
      const showNavButtons = maxButtonsWithNav >= 5;

      // Step 2: Calculate how many page numbers we can actually show
      const availableSpace = showNavButtons ? spaceWithNavButtons : spaceWithoutNavButtons;
      const maxPageButtons = Math.floor(availableSpace / (pageButtonWidth + spacing));

      // Cap at 10 for usability (or totalPages if less)
      const effectiveMaxButtons = Math.min(maxPageButtons, 10, totalPages);

      // Step 3: Generate the visible page numbers
      const visiblePageNumbers = getVisiblePageNumbers(currentPage, totalPages, effectiveMaxButtons);

      // Update state with our calculations
      setPaginationState({
        visiblePageNumbers,
        showNavButtons
      });
    };

    // Initial calculation
    calculatePagination();

    // Recalculate when window resizes
    window.addEventListener('resize', calculatePagination);
    return () => window.removeEventListener('resize', calculatePagination);
  }, [currentPage, totalPages]);

  // Determine which page numbers to show
  const getVisiblePageNumbers = (current, total, maxVisible) => {
    console.log("get visible page number ", current, total, maxVisible);

    // For small number of pages, show all
    if (total <= maxVisible) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    // Always show first and last pages
    // This leaves (maxVisible - 2) slots for other pages and ellipses
    const middleSlots = maxVisible - 2;

    // Determine if we need ellipses and how many
    const needStartEllipsis = current > 2 + Math.floor(middleSlots / 2);
    const needEndEllipsis = current < total - 1 - Math.floor(middleSlots / 2);

    // Calculate how many middle page numbers we can show
    let middleCount = middleSlots;
    //if (needStartEllipsis) middleCount--;
    //if (needEndEllipsis) middleCount--;

    // Calculate the range of middle pages to show
    const middleStart = Math.max(2, Math.min(
      current - Math.floor(middleCount / 2),
      total - middleCount - 1
    ));
    const middleEnd = Math.min(total - 1, middleStart + middleCount - 1);

    // Build the final array of page numbers
    const result = [1]; // Always include first page

    if (needStartEllipsis) {
      // Add ellipsis after first page
      result.push('ellipsis-start');
    } else {
      // No start ellipsis needed, add page 2 if not in middle range
      if (middleStart > 2) result.push(2);
    }

    // Add middle pages
    for (let i = middleStart; i <= middleEnd; i++) {
      result.push(i);
    }

    if (needEndEllipsis) {
      // Add ellipsis before last page
      result.push('ellipsis-end');
    } else {
      // No end ellipsis needed, add second-to-last page if not in middle range
      if (middleEnd < total - 1) result.push(total - 1);
    }

    // Always include last page
    if (total > 1) result.push(total);

    return result;
  };

  return (
    <nav ref={paginationRef} className="flex items-center justify-center space-x-1 sm:space-x-2 w-full">
      {paginationState.showNavButtons && (
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`p-2 rounded-lg border border-gray-200 ${currentPage === 1
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}

      <div className="flex items-center space-x-1">
        {paginationState.visiblePageNumbers.map((page, index) => {
          // Render ellipsis
          if (page === 'ellipsis-start' || page === 'ellipsis-end') {
            return (
              <span key={`ellipsis-${index}`} className="text-gray-500">
                ...
              </span>
            );
          }

          // Render page number buttons
          return (
            <button
              key={`page-${page}`}
              onClick={() => onPageChange(page)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${currentPage === page
                  ? 'bg-gradient-to-r from-[#24ccf2] to-[#2476f2] text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-50'
                }`}
            >
              {page}
            </button>
          );
        })}
      </div>

      {paginationState.showNavButtons && (
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-lg border border-gray-200 ${currentPage === totalPages
              ? 'text-gray-300 cursor-not-allowed'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          aria-label="Next page"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
    </nav>
  );
};

interface ListingResultsViewProps {
  localities: string[];
  listings: Array<Listing>;
  isLoading: boolean;
  pageSize: number;
  pagination?: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    snapshotTimestamp: string;
  };
  onPageChange: (page: number) => void;
}

// Interface for our display items
interface DisplayItem {
  stableId: string; // This ID never changes for this position
  listing: Listing | null; // Listing null implies loading
}

// New component for the results count with skeleton state
const ResultsCount = ({ isLoading, pagination, totalItems, listingsLength }) => {
  // When loading, show a skeleton placeholder with the same dimensions
  if (isLoading || !pagination) {
    return (
      <div className="text-sm text-gray-600">
        <div className="h-5 w-48 bg-gray-200 rounded animate-pulse"></div>
      </div>
    );
  }

  // When loaded, show the actual results count
  const start = (pagination.currentPage - 1) * totalItems + 1;
  const calculatedEnd = start + listingsLength - 1;
  
  return (
    <div className="text-sm text-gray-600">
      Showing {start}-{calculatedEnd} of {pagination.totalItems} results
    </div>
  );
};

const ListingResultsView = ({ localities, listings, isLoading, pageSize, pagination, onPageChange }) => {
  // Create a ref for the previous listings to detect page changes
  const prevPageRef = useRef(pagination?.currentPage || 1);

  // Create state to hold our display items (with stable IDs)
  const [displayItems, setDisplayItems] = useState<Array<DisplayItem>>(() =>
    Array(pageSize).fill(null).map((_, index) => ({
      stableId: `position-${index}`, // Stable ID based on position that never changes
      listing: null
    }))
  );

  // Update display items when listings or loading state changes
  // First effect - handle page changes and set loading state
  useEffect(() => {
    // Check if we've changed pages
    const currentPage = pagination?.currentPage || 1;
    const isPageChange = prevPageRef.current !== currentPage;
    prevPageRef.current = currentPage;

    // If we're loading or changed pages, set everything to loading state
    if (isLoading || isPageChange) {
      setDisplayItems(prev =>
        Array(pageSize).fill(null).map((_, index) => ({
          stableId: `position-${index}`,
          listing: null
        }))
      );
    }
  }, [isLoading, pageSize, pagination?.currentPage]);

  // Second effect - handle updating display items when loading finishes
  useEffect(() => {
    // Only update when NOT loading (loading has finished)
    if (!isLoading) {
      if (listings.length > 0) {
        // Filter valid listings
        const validListings = listings.filter(
          listing => listing.id && listing.rent && listing.suburb && listing.postcode && listing.availableFrom
        );

        // Create a new array with exactly the number of items we need
        const newDisplayItems = validListings.map((listing, index) => ({
          stableId: `position-${index}`,
          listing
        }));

        // Set the display items to just the valid listings
        setDisplayItems(newDisplayItems);
      } else {
        // No results
        setDisplayItems([]);
      }
    }
  }, [listings, isLoading, pageSize]);

  // Show empty state if no items to display
  if (!isLoading && listings.length === 0) {
    return <NoResultsFound />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Filters bar - FIXED with skeleton for results count */}
      <div className="mb-6 flex items-center justify-between">
        <ResultsCount 
          isLoading={isLoading}
          pagination={pagination}
          totalItems={pageSize}
          listingsLength={listings.length}
        />
        {false &&
          <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors duration-200">
            <SlidersHorizontal className="h-4 w-4" />
            Filters
          </button>
        }
      </div>

      {/* Grid of listings - using STABLE KEYS that NEVER change */}
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {displayItems.map(item => (
          <ListingCard
            key={item.stableId} // This key never changes for a given position
            listing={item.listing}
          />
        ))}
      </div>

      {/* Always show pagination when we have results */}
      {pagination && (
        <div className="mt-8 flex justify-center">
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ListingResultsView;